import { Link } from "react-router-dom";

interface VideoCardProps {
  id: string;
  title: string;
  cover: string;
  streamerName: string;
  streamerAvatar: string;
  duration: number;
  views: number;
  publishedLabel?: string;
  subcategory?: string;
  description?: string;
  variant?: "grid" | "list";
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function formatViews(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万次播放`;
  return `${n}次播放`;
}

export default function VideoCard({
  id,
  title,
  cover,
  streamerName,
  streamerAvatar,
  duration,
  views,
  publishedLabel,
  subcategory,
  description,
  variant = "grid",
}: VideoCardProps) {
  if (variant === "list") {
    return (
      <Link
        to={`/video/${id}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-800 bg-background-900 p-3 transition-colors hover:border-primary-600/70 sm:flex-row"
      >
        <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-800 sm:h-28 sm:w-52">
          <img src={cover} alt={title} title={`${title} 主播视频`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute bottom-2 right-2 rounded bg-background-950/85 px-1.5 py-0.5 font-label text-[11px] font-semibold text-foreground-100">
            {formatDuration(duration)}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
          <h3 className="line-clamp-2 font-heading text-sm font-bold text-foreground-100 transition-colors group-hover:text-primary-400 md:text-base">
            {title}
          </h3>
          {description && (
            <p className="line-clamp-2 text-xs text-foreground-500 md:text-sm">{description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500/20 font-label text-[10px] font-bold text-primary-400">
                {streamerAvatar}
              </span>
              {streamerName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-play-circle-line" /> {formatViews(views)}
            </span>
            {publishedLabel && <span>{publishedLabel}</span>}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/video/${id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors hover:border-primary-600/70"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-background-800">
        <img
          src={cover}
          alt={title}
          title={`${title} 主播视频`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="absolute bottom-2 right-2 rounded bg-background-950/85 px-1.5 py-0.5 font-label text-[11px] font-semibold text-foreground-100">
          {formatDuration(duration)}
        </span>
        {subcategory && (
          <span className="absolute left-2 top-2 rounded-full bg-background-950/80 px-2 py-0.5 text-[11px] font-medium text-secondary-300">
            {subcategory}
          </span>
        )}
        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-950">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3 md:p-4">
        <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-100 transition-colors group-hover:text-primary-400 md:text-base">
          {title}
        </h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1 text-xs text-foreground-500">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500/20 font-label text-[10px] font-bold text-primary-400">
              {streamerAvatar}
            </span>
            <span className="truncate">{streamerName}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <i className="ri-play-circle-line" /> {formatViews(views)}
          </span>
        </div>
      </div>
    </Link>
  );
}